import { StringFilterBuilder } from '@/shared/filters/entity-filters/string-filter-builder/string-filter-builder';
import {
  BETWEEN_SEPARATOR,
  IN_SEPARATOR,
} from '@/shared/filters/filter-builder.constants';
import { IFilterBuilder } from '@/shared/filters/filter-builder.interfaces';
import {
  StringFilterMethod,
  TFilterFieldMapping,
  TValueTransformerMapping,
} from '@/shared/filters/filter-builder.types';

export abstract class FilterBuilder<
  T,
  This extends FilterBuilder<T, This>,
> implements IFilterBuilder<T> {
  protected filter: Partial<T> = {};

  // Define which filter keys are allowed for this specific filter builder
  abstract allowedFilters: Array<keyof T>;

  // Define field mappings (e.g., 'name' -> 'nameLower')
  protected fieldMappings: TFilterFieldMapping<T> = {};

  // Define value transformers (e.g., transform 'name' to lowercase)
  protected valueTransformers: TValueTransformerMapping<T> = {};

  reset(): undefined {
    this.filter = {};
    return undefined;
  }

  /**
   * Build and return the final filter object
   * @returns The complete filter object or undefined if empty
   */
  build(): T | undefined {
    return Object.keys(this.filter).length ? (this.filter as T) : undefined;
  }

  /**
   * Apply a filter with the specified type, value, and method
   * @param type The filter property to apply
   * @param value The filter value(s)
   * @param filterMethod The filtering method to use
   * @returns The filter builder instance for chaining
   */
  applyFilter(
    type: keyof T,
    value: string | string[],
    filterMethod: StringFilterMethod = 'contains',
  ): IFilterBuilder<T> {
    // Skip if the filter type isn't allowed or no value is provided
    if (!this.allowedFilters.includes(type) || !value) {
      return this;
    }

    // Apply value transformation if defined
    const transformedValue = this.transformValue(type, value);

    // Create and configure the string builder with the appropriate filter method
    const stringBuilder = this.createStringBuilder(
      transformedValue,
      filterMethod,
    );

    // Get the actual field name to use (mapping or original)
    const fieldName = (this.fieldMappings[type] || type) as keyof T;

    // Apply the string filter to the property
    this.applyStringFilter(fieldName, stringBuilder);

    return this;
  }

  /**
   * Transform a value using the defined transformer for the field
   * @param type The field type
   * @param value The value to transform
   * @returns The transformed value
   */
  protected transformValue(
    type: keyof T,
    value: string | string[],
  ): string | string[] {
    const transformer = this.valueTransformers[type];
    if (!transformer) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((v) => transformer(v));
    }

    return transformer(value);
  }

  /**
   * Apply a string filter to a specific property
   * @param property The property to filter
   * @param builder The string filter builder
   * @returns The filter builder instance for chaining
   */
  protected applyStringFilter<K extends keyof T>(
    property: K,
    builder: StringFilterBuilder,
  ): This {
    const builtFilter = builder.build();
    if (builtFilter) {
      // Fix: Use spread operator to avoid read-only property assignment
      this.filter = {
        ...this.filter,
        [property]: builtFilter,
      } as Partial<T>;
    }
    return this as unknown as This;
  }

  /**
   * Create and configure a StringFilterBuilder with the provided value and method
   * @param value The filter value(s)
   * @param filterMethod The filtering method to use
   * @returns A configured StringFilterBuilder
   */
  private createStringBuilder(
    value: string | string[],
    filterMethod: StringFilterMethod,
  ): StringFilterBuilder {
    const stringBuilder = new StringFilterBuilder();

    if (filterMethod === 'in' || filterMethod === 'between') {
      const separator =
        filterMethod === 'in' ? IN_SEPARATOR : BETWEEN_SEPARATOR;
      // Handle array values for 'in' method
      const values = this.normalizeArrayValues(value, separator);
      (stringBuilder as any)[filterMethod](values);
    } else if (filterMethod === 'doNotIgnoreNullable') {
      // Handle nullable values
      const values = Array.isArray(value) ? value : [value.trim()];
      (stringBuilder as any)[filterMethod](values);
    } else {
      // Handle standard string filter methods
      const stringValue = Array.isArray(value) ? value.join(',') : value;
      (stringBuilder as any)[filterMethod](stringValue.trim());
    }

    return stringBuilder;
  }

  /**
   * Normalize a value or array of values for the 'in' filter method
   * @param value The input value(s)
   * @param separator The separator used, to split the string into an array
   * @returns A normalized array of values
   */
  private normalizeArrayValues(
    value: string | string[],
    separator: string,
  ): string[] {
    if (Array.isArray(value)) {
      return value;
    }

    return value
      .split(separator)
      .map((val) => val.trim())
      .filter(Boolean);
  }
}
